import type {TextProvider, TextProviderChoice} from "./ai-providers";
import type {MediaOutputType} from "./media";

export type MediaModelDefaults = Record<TextProvider, string> & {image: string};
export const mediaModelDefaults: MediaModelDefaults = {
  openai: "gpt-5.6-terra",
  anthropic: "claude-sonnet-5",
  openrouter: "anthropic/claude-sonnet-5",
  image: "gpt-image-2",
};
export const mediaProviderLabels: Record<TextProvider, string> = {openai:"OpenAI", anthropic:"Anthropic", openrouter:"OpenRouter"};

// Unknown model families are not assumed to accept images. Text-only requests
// can still use a custom model; visual requests use the known vision fallback.
export function supportsMediaReferences(choice: TextProviderChoice): boolean {
  const model = choice.model || mediaModelDefaults[choice.provider];
  if (choice.provider === "openrouter") {
    const [provider, ...parts] = model.split("/");
    return (provider === "openai" || provider === "anthropic")
      && supportsMediaReferences({provider, model:parts.join("/").replace(/:.*$/, "")});
  }
  if (choice.provider === "anthropic") return /^claude-(?:[3-9](?:[.-]|$)|(?:opus|sonnet|haiku)-[3-9](?:[.-]|$))/.test(model);
  return /^(?:gpt-(?:4o|4\.1|5)(?:[.-]|$)|o3(?:[.-]|$)|o4-mini(?:[.-]|$))/.test(model)
    && !/(audio|realtime|transcribe|tts|image)/i.test(model);
}

export function mediaImageModel(requested?: string, fallback = mediaModelDefaults.image): string {
  const supported = (model: string) => /^gpt-image-(?:1(?:\.5)?(?:-mini)?|2)(?:-\d{4}-\d{2}-\d{2})?$/.test(model);
  return requested && supported(requested) ? requested : supported(fallback) ? fallback : mediaModelDefaults.image;
}

export type MediaModelRouting = {
  requestedWriter: TextProviderChoice;
  writer: Required<TextProviderChoice>;
  referenceAnalysis: Required<TextProviderChoice> | null;
  image: {provider:"openai"; model:string} | null;
  writerFallback?: string;
  imageFallback?: string;
};

export function resolveMediaModelRouting(input: {
  mediaType: MediaOutputType;
  writer: TextProviderChoice;
  referenceCount?: number;
  imageMode?: "typographic" | "illustrated";
  imageModel?: string;
}, defaults: MediaModelDefaults = mediaModelDefaults): MediaModelRouting {
  const requested = {provider:input.writer.provider, model:input.writer.model || defaults[input.writer.provider]};
  const usesReferences = input.mediaType === "instagram-post" && (input.referenceCount ?? 0) > 0;
  const mediaOnlyModel = /(?:^|\/)(?:gpt-image-|dall-e-|tts-|whisper-)|(?:transcribe|realtime)/i.test(requested.model);
  const fallback = mediaOnlyModel || usesReferences && !supportsMediaReferences(requested);
  const fallbackModel = supportsMediaReferences({provider:"openai",model:defaults.openai}) ? defaults.openai : mediaModelDefaults.openai;
  const writer: Required<TextProviderChoice> = fallback ? {provider:"openai", model:fallbackModel} : requested;
  const createsImage = input.mediaType === "instagram-reel" || input.mediaType === "instagram-post" && input.imageMode === "illustrated";
  const image = createsImage ? {provider:"openai" as const, model:mediaImageModel(input.imageModel, defaults.image)} : null;
  return {
    requestedWriter:requested,
    writer,
    referenceAnalysis:usesReferences ? writer : null,
    image,
    ...(fallback ? {writerFallback:mediaOnlyModel ? "OpenAI handles writing because the selected model is for another media capability." : "Image input is not verified for the selected model. OpenAI handles the references and writing."} : {}),
    ...(image && input.imageModel && image.model !== input.imageModel ? {imageFallback:"The selected image model is unsupported. A compatible OpenAI image model is used."} : {}),
  };
}
